import { Injectable, Renderer2, RendererFactory2, Inject } from '@angular/core';
import { Subject, Observable, Subscriber } from 'rxjs';
import { DOCUMENT } from '@angular/platform-browser';
import { ConfigService } from './config.service';
import { SessionService } from './session.service';

@Injectable()
export class SilentAuthService {

    private renderer: Renderer2;
    private frameRef: any;
    private iframeDisposed$ = new Subject<void>();
    private isFrameLoaded = false;
    private ONE_HOUR_SECONDS = 36e2;

    constructor(
        rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private document: Document,
        private configService: ConfigService,
        private sessionService: SessionService
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    validateAndRefreshTokenIfNeeded(currentToken?: string, issuedAt?: number): Observable<AuthResult> {
        const isValidToken = currentToken || (issuedAt && !this.requiresRefresh(currentToken));
        if (isValidToken) {
        return Observable.of(new AuthResult(AuthAttemptStatus.SUCCESS, currentToken, issuedAt));
        }
        return this.validateAndRefreshTokenInMasterStorage();
    }

    validateAndRefreshTokenInMasterStorage(): Observable<AuthResult> {
        return this.trySilentAuth().takeUntil(this.iframeDisposed$);
    }

    private requiresRefresh(jwt: string | null): boolean {
        const claims = this.sessionService.decodeToken(jwt);
        const expiresAt = claims ? claims.exp : null;
        if (!expiresAt || isNaN(expiresAt)) {
          return true;
        }
    
        const currentEpochTime = Math.round(new Date().getTime() / 1000.0);
        const secondsLeft = expiresAt - currentEpochTime;
        return secondsLeft / this.ONE_HOUR_SECONDS < 1;
    };

    private trySilentAuth(): Observable<AuthResult> {
        if (!this.frameRef) {
            this.frameRef = this.renderer.createElement('iframe');
            this.renderer.setAttribute(this.frameRef, 'src', `${this.configService.get('server').account}/silent`);
            this.renderer.setStyle(this.frameRef, 'display', 'none');
            this.renderer.appendChild(this.document.body, this.frameRef);
        }

        return this.frameLoaded()
            .do(() => (this.isFrameLoaded = true))
            .mergeMap(() => this.checkAccessToken())
            .take(1);
    }

    private frameLoaded(): Observable<void> {
        if (this.isFrameLoaded) {
        return Observable.of(null);
        }

        return Observable.create(subscriber => {
            return this.renderer.listen(this.frameRef, 'load', () => {
                subscriber.next();
                subscriber.complete();
            });
        });
    }

    private checkAccessToken(): Observable<AuthResult> {
        return Observable.create((subscriber: Subscriber<AuthResult>) => {
            const messageListenerRef = this.renderer.listen('window', 'message', event => {
                const { data, origin } = event;
                if (origin !== this.configService.get('server').account) return;
                const { msg = null, accessToken = null, issuedAt = 0 } = data || {};
                if (msg === 'success') {
                    subscriber.next(new AuthResult(AuthAttemptStatus.SUCCESS, accessToken, issuedAt));
                } else if (msg === 'unauthorized') {
                    subscriber.next(new AuthResult(AuthAttemptStatus.UNAUTHORIZED));
                } else {
                    subscriber.next(new AuthResult(AuthAttemptStatus.ERROR));
                }

                subscriber.complete();
            });
            
            let url = this.configService.get('server').account;
            this.frameRef.contentWindow.postMessage('request_creds', url);
            return messageListenerRef;
        });
    }

    dispose() {
        if (this.frameRef) {
            this.renderer.removeChild(this.document, this.frameRef);
            this.iframeDisposed$.next();
        }
    }
}

export class AuthResult {
    constructor(
        public readonly status: AuthAttemptStatus,
        public readonly accessToken?: string,
        public readonly issuedAt?: number
    ) {}
}

export enum AuthAttemptStatus {
    SUCCESS,
    UNAUTHORIZED,
    ERROR
}



// WEBPACK FOOTER //
// ./src/app/core/services/silent-auth.service.ts